import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaSync } from 'react-icons/fa';
import { CORSSmartClient } from '../../utils/corsFix';
import './CORSFix.css';

const CORSFix = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [corsStatus, setCorsStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [fixes, setFixes] = useState([]);

  useEffect(() => {
    // Check for CORS errors in console
    const checkForCORSErrors = () => {
      const hasCORSErrors = window.performance.getEntriesByType('navigation').some(entry => 
        entry.name.includes('api-aovv3tvopa-uc.a.run.app')
      );
      
      if (hasCORSErrors) {
        setIsVisible(true);
      }
    };

    checkForCORSErrors();
  }, []);

  const checkCORSStatus = async () => {
    setIsChecking(true);
    try {
      // Simple status check for localhost API
      const endpoints = ['/health', '/api/products', '/api/customers'];
      const status = {};
      
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(`http://localhost:5000${endpoint}`);
          status[`localhost${endpoint}`] = {
            status: response.ok ? 'success' : 'failed',
            statusCode: response.status
          };
        } catch (error) {
          status[`localhost${endpoint}`] = {
            status: 'error',
            error: error.message
          };
        }
      }
      
      setCorsStatus(status);
      
      // Generate fixes based on status
      const newFixes = [];
      
      Object.entries(status).forEach(([key, result]) => {
        if (result.status === 'success') {
          newFixes.push({
            type: 'success',
            message: `✅ Localhost API working for ${key}`,
            action: 'Keep using localhost API'
          });
        } else {
          newFixes.push({
            type: 'warning',
            message: `⚠️ Localhost API issue for ${key}`,
            action: 'Check localhost server'
          });
        }
      });
      
      setFixes(newFixes);
    } catch (error) {
      console.error('CORS status check failed:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const applyCORSFix = () => {
    // Force reload with localhost API
    localStorage.setItem('forceLocalhostAPI', 'true');
    window.location.reload();
  };

  const testLocalhostAPI = async () => {
    try {
      const client = new CORSSmartClient();
      const response = await client.get('/health');
      
      if (response.ok) {
        alert('✅ Localhost API is working!');
      } else {
        alert('❌ Localhost API returned error: ' + response.status);
      }
    } catch (error) {
      alert('❌ Localhost API test failed: ' + error.message);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="cors-fix-overlay">
      <div className="cors-fix-modal">
        <div className="cors-fix-header">
          <FaExclamationTriangle className="cors-fix-icon" />
          <h3>CORS Policy Issues Detected</h3>
          <button 
            className="cors-fix-close"
            onClick={() => setIsVisible(false)}
          >
            <FaTimesCircle />
          </button>
        </div>

        <div className="cors-fix-content">
          <div className="cors-fix-description">
            <p>
              <FaInfoCircle className="info-icon" />
              Your application is experiencing CORS (Cross-Origin Resource Sharing) issues 
              when trying to access the Firebase Functions API from localhost.
            </p>
          </div>

          <div className="cors-fix-actions">
            <button 
              className="cors-fix-button primary"
              onClick={checkCORSStatus}
              disabled={isChecking}
            >
              {isChecking ? (
                <>
                  <FaSync className="spinning" />
                  Checking Status...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Check CORS Status
                </>
              )}
            </button>

            <button 
              className="cors-fix-button secondary"
              onClick={testLocalhostAPI}
            >
              <FaInfoCircle />
              Test Localhost API
            </button>

            <button 
              className="cors-fix-button success"
              onClick={applyCORSFix}
            >
              <FaCheckCircle />
              Apply CORS Fix
            </button>
          </div>

          {corsStatus && (
            <div className="cors-fix-status">
              <h4>CORS Status Results:</h4>
              <div className="status-grid">
                {Object.entries(corsStatus).map(([key, result]) => (
                  <div key={key} className={`status-item ${result.status}`}>
                    <div className="status-key">{key}</div>
                    <div className="status-result">
                      {result.status === 'success' && <FaCheckCircle className="success" />}
                      {result.status === 'error' && <FaTimesCircle className="error" />}
                      {result.status === 'failed' && <FaExclamationTriangle className="warning" />}
                      <span>{result.status}</span>
                      {result.statusCode && <span>({result.statusCode})</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {fixes.length > 0 && (
            <div className="cors-fix-suggestions">
              <h4>Recommended Fixes:</h4>
              <ul>
                {fixes.map((fix, index) => (
                  <li key={index} className={`fix-item ${fix.type}`}>
                    <span className="fix-message">{fix.message}</span>
                    <span className="fix-action">{fix.action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="cors-fix-info">
            <h4>What is CORS?</h4>
            <p>
              CORS (Cross-Origin Resource Sharing) is a security feature that prevents 
              web pages from making requests to a different domain than the one serving 
              the web page. This is causing issues when your localhost frontend tries 
              to access the Firebase Functions API.
            </p>
            
            <h4>Solutions:</h4>
            <ul>
              <li><strong>Use Localhost API:</strong> Switch to your local server (recommended for development)</li>
              <li><strong>Configure Firebase Functions:</strong> Add CORS headers to your Firebase Functions</li>
              <li><strong>Use a Proxy:</strong> Set up a proxy server to handle CORS</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CORSFix;
